import React, { useState, useEffect, useRef, useCallback } from 'react';

const FloatingPhone = () => {
    const [isMobile, setIsMobile] = useState(false);
    const [position, setPosition] = useState({ x: 50, y: 50 });
    const [direction, setDirection] = useState({ x: 1, y: 1 });
    // Phone is always visible now
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isFloating, setIsFloating] = useState(true);
    const phoneRef = useRef(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isCalling, setIsCalling] = useState(false);
    const [showWebsite, setShowWebsite] = useState(false);

    // Mobile detection and positioning
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            
            if (mobile) {
                // Position phone in bottom right on mobile
                setPosition({
                    x: window.innerWidth - 150, // 150px from right edge
                    y: window.innerHeight - 250 // 250px from bottom
                });
                setIsFloating(false); // Disable floating on mobile
            } else {
                setIsFloating(true); // Enable floating on desktop
            }
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Floating animation
    useEffect(() => {
        if (!isFloating || isDragging || isMobile) return;

        const movePhone = () => {
            setPosition(prev => {
                const newX = prev.x + direction.x;
                const newY = prev.y + direction.y;

                // Bounce off edges
                if (newX <= 0 || newX >= window.innerWidth - 80) {
                    setDirection(prev => ({ ...prev, x: -prev.x }));
                }
                if (newY <= 0 || newY >= window.innerHeight - 120) {
                    setDirection(prev => ({ ...prev, y: -prev.y }));
                }

                return { x: newX, y: newY };
            });
        };

        const interval = setInterval(movePhone, 80);
        return () => clearInterval(interval);
    }, [direction, isFloating, isDragging]);

    // Drag functionality for both mouse and touch
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
        setIsFloating(false);
        const rect = phoneRef.current.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        setDragOffset({
            x: clientX - rect.left,
            y: clientY - rect.top
        });
    }, []);

    const handleTouchStart = useCallback((e) => {
        e.preventDefault();
        handleMouseDown(e);
    }, [handleMouseDown]);

    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            const clientX = e.clientX || (e.touches && e.touches[0].clientX);
            const clientY = e.clientY || (e.touches && e.touches[0].clientY);

            // Constrain to screen bounds
            const phoneWidth = isMobile ? 120 : 150; // Smaller on mobile
            const phoneHeight = isMobile ? 200 : 250; // Smaller on mobile
            const newX = Math.max(0, Math.min(clientX - dragOffset.x, window.innerWidth - phoneWidth));
            const newY = Math.max(0, Math.min(clientY - dragOffset.y, window.innerHeight - phoneHeight));

            setPosition({
                x: newX,
                y: newY
            });
        }
    }, [isDragging, dragOffset]);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        handleMouseMove(e);
    }, [handleMouseMove]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            // Resume floating after a delay
            setTimeout(() => setIsFloating(true), 1000);
        }
    }, [isDragging]);

    const handleTouchEnd = useCallback((e) => {
        e.preventDefault();
        handleMouseUp();
    }, [handleMouseUp]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd, { passive: false });
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // Handle key press
    const handleKeyPress = (key) => {
        if (isCalling) return;
        setPhoneNumber(prev => prev + key);
    };

    // Handle call
    const handleCall = () => {
        if (phoneNumber === '6178888888') {
            setIsCalling(true);
            setTimeout(() => {
                // Show the website popup
                setShowWebsite(true);
                setIsCalling(false);
                setPhoneNumber('');
            }, 2000);
        } else if (phoneNumber.length > 0) {
            setIsCalling(true);
            setTimeout(() => {
                alert('The number is wrong');
                setIsCalling(false);
                setPhoneNumber('');
            }, 2000);
        }
    };

    // Close website popup
    const closeWebsite = () => {
        setShowWebsite(false);
    };


    return (
        <div
            ref={phoneRef}
            className={`floating-phone ${isDragging ? 'dragging' : ''} ${isMobile ? 'mobile-phone' : ''}`}
            style={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                zIndex: 999,
                cursor: isDragging ? 'grabbing' : 'grab',
                transition: isDragging ? 'none' : 'all 0.3s ease'
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
        >
            <div className="nokia-phone">
                <div className="phone-screen">
                    <div className="screen-content">
                        <div className="signal-bars">
                            <div className="bar"></div>
                            <div className="bar"></div>
                            <div className="bar"></div>
                        </div>
                        <div className="battery">🔋</div>
                    </div>
                    <div className="screen-text">
                        {isCalling ? 'Calling...' : phoneNumber || 'GOOD VIBES* #ccfc'}
                    </div>
                    <div className="screen-time">12:34</div>
                </div>
                <div className="phone-keypad">
                    <div className="key-row">
                        <div className="key" data-key="1" onClick={() => handleKeyPress('1')}>1</div>
                        <div className="key" data-key="2" onClick={() => handleKeyPress('2')}>2</div>
                        <div className="key" data-key="3" onClick={() => handleKeyPress('3')}>3</div>
                    </div>
                    <div className="key-row">
                        <div className="key" data-key="4" onClick={() => handleKeyPress('4')}>4</div>
                        <div className="key" data-key="5" onClick={() => handleKeyPress('5')}>5</div>
                        <div className="key" data-key="6" onClick={() => handleKeyPress('6')}>6</div>
                    </div>
                    <div className="key-row">
                        <div className="key" data-key="7" onClick={() => handleKeyPress('7')}>7</div>
                        <div className="key" data-key="8" onClick={() => handleKeyPress('8')}>8</div>
                        <div className="key" data-key="9" onClick={() => handleKeyPress('9')}>9</div>
                    </div>
                    <div className="key-row">
                        <div className="key" data-key="*" onClick={() => handleKeyPress('*')}>*</div>
                        <div className="key" data-key="0" onClick={() => handleKeyPress('0')}>0</div>
                        <div className="key" data-key="#" onClick={() => handleKeyPress('#')}>#</div>
                    </div>
                    <div className="call-button-row">
                        <div className="call-button" onClick={handleCall}>
                            {isCalling ? 'Calling...' : 'CALL'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Website Popup with iframe */}
            {showWebsite && (
                <div className="website-popup">
                    <div className="popup-header">
                        <h3>cc-fc.cc</h3>
                        <button className="close-popup" onClick={closeWebsite}>×</button>
                    </div>
                    <div className="popup-content">
                        <iframe
                            src="https://cc-fc.cc/"
                            title="cc-fc.cc"
                            className="website-iframe"
                            frameBorder="0"
                            allowFullScreen
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FloatingPhone;
